/**
 * R1-CORR-03 / C04-3 / C04-6 — generation-gated desk ownership + pending-flag handoff.
 * Page loads and per-column reloads share a model that preserves column independence:
 * - bumping a column gen invalidates only that column's in-flight paints
 * - starting a page load bumps every column gen so stale column/page paints cannot win
 * - pending flags must transfer to the replacement owner (never leave stuck true)
 */

export function createDeskGenerationGate(initial = 0) {
  let current = initial;
  return {
    next() {
      current += 1;
      return current;
    },
    isCurrent(gen) {
      return current === gen;
    },
    get current() {
      return current;
    },
  };
}

/**
 * Apply a progressive column update only when the generation still owns the desk.
 * @returns {boolean} whether the update was applied
 */
export function applyIfCurrent(gate, gen, applyFn) {
  if (!gate.isCurrent(gen)) {
    return false;
  }
  applyFn();
  return true;
}

/**
 * Mutable ownership state for page + per-column desk loads.
 * @param {string[]} columnIds
 */
export function createDeskLoadOwnership(columnIds) {
  let pageGen = 0;
  const columnGen = Object.fromEntries((columnIds || []).map((id) => [id, 0]));

  return {
    /** Begin a full-page default/search load; invalidates all in-flight column paints. */
    beginPageLoad() {
      pageGen += 1;
      Object.keys(columnGen).forEach((id) => {
        columnGen[id] += 1;
      });
      return {
        pageGen,
        columnGens: { ...columnGen },
        /** C04-6: page owns loading; clear all column auto-search pending flags. */
        pendingPatch: {
          clearAutoSearchPendingAll: true,
        },
      };
    },

    /** Begin a single-column reload (preset / auto-search / idle); does not touch other columns. */
    beginColumnLoad(columnId) {
      if (!(columnId in columnGen)) {
        columnGen[columnId] = 0;
      }
      columnGen[columnId] += 1;
      return {
        columnId,
        columnGen: columnGen[columnId],
        /** C04-6: column takes over — clear stuck page fetch-pending for this column. */
        pendingPatch: {
          clearColumnFetchPending: columnId,
        },
      };
    },

    isPageCurrent(gen) {
      return pageGen === gen;
    },

    isColumnCurrent(columnId, gen) {
      return columnGen[columnId] === gen;
    },

    /** Page-driven progressive paint: both page epoch and that column's snapshot must match. */
    canApplyPageColumn(pageGenSnap, columnId, columnGenSnap) {
      return pageGen === pageGenSnap && columnGen[columnId] === columnGenSnap;
    },

    get pageGen() {
      return pageGen;
    },

    columnGenOf(columnId) {
      return columnGen[columnId] ?? 0;
    },
  };
}

/**
 * Apply ownership pending-flag patches (pure; for DeskPage + tests).
 * @returns {{ autoSearchPendingMap: object, columnFetchPendingMap: object }}
 */
export function applyPendingOwnershipPatch(
  { autoSearchPendingMap = {}, columnFetchPendingMap = {} },
  patch,
  columnIds = [],
) {
  let nextAuto = { ...autoSearchPendingMap };
  let nextFetch = { ...columnFetchPendingMap };
  if (!patch) {
    return { autoSearchPendingMap: nextAuto, columnFetchPendingMap: nextFetch };
  }
  if (patch.clearAutoSearchPendingAll) {
    const ids = columnIds.length ? columnIds : Object.keys(nextAuto);
    nextAuto = Object.fromEntries(ids.map((id) => [id, false]));
  }
  if (patch.clearColumnFetchPending) {
    nextFetch = { ...nextFetch, [patch.clearColumnFetchPending]: false };
  }
  if (patch.setAutoSearchPending) {
    const { columnId, value } = patch.setAutoSearchPending;
    nextAuto = { ...nextAuto, [columnId]: value };
  }
  if (patch.setColumnFetchPending) {
    const { columnId, value } = patch.setColumnFetchPending;
    nextFetch = { ...nextFetch, [columnId]: value };
  }
  return { autoSearchPendingMap: nextAuto, columnFetchPendingMap: nextFetch };
}

/**
 * Deterministic harness: stale page refresh must not overwrite a newer column selection.
 * @returns {{ overwritten: boolean, finalArticles: unknown }}
 */
export function simulateColumnPresetVsStalePageRefresh({
  columnId = 'markets',
  stalePageArticles = [{ id: 'stale' }],
  presetArticles = [{ id: 'preset' }],
} = {}) {
  const own = createDeskLoadOwnership(['markets', 'geopolitics', 'techscience']);
  const state = { [columnId]: null };

  const page = own.beginPageLoad();
  // User changes category while page load is in flight
  const col = own.beginColumnLoad(columnId);
  if (own.isColumnCurrent(columnId, col.columnGen)) {
    state[columnId] = presetArticles;
  }
  // Stale page column callback arrives later
  const overwritten = own.canApplyPageColumn(page.pageGen, columnId, page.columnGens[columnId]);
  if (overwritten) {
    state[columnId] = stalePageArticles;
  }
  return { overwritten, finalArticles: state[columnId] };
}

/**
 * C04-6 — characterize pending-flag handoffs (both orders).
 */
export function simulatePendingFlagHandoffs(columnIds = ['markets', 'geopolitics', 'techscience']) {
  const own = createDeskLoadOwnership(columnIds);
  const columnId = 'markets';

  // Path 1: column pending → page load supersedes → column finally skips → flags must not stick
  let flags = {
    autoSearchPendingMap: Object.fromEntries(columnIds.map((id) => [id, false])),
    columnFetchPendingMap: Object.fromEntries(columnIds.map((id) => [id, false])),
  };
  const col1 = own.beginColumnLoad(columnId);
  flags = applyPendingOwnershipPatch(flags, col1.pendingPatch, columnIds);
  flags = applyPendingOwnershipPatch(
    flags,
    { setAutoSearchPending: { columnId, value: true } },
    columnIds,
  );
  const page = own.beginPageLoad();
  flags = applyPendingOwnershipPatch(flags, page.pendingPatch, columnIds);
  flags = applyPendingOwnershipPatch(
    flags,
    {
      setColumnFetchPending: { columnId, value: true },
    },
    columnIds,
  );
  // stale column finally would skip clear — without page patch, autoSearch would stick true
  const path1StuckWithoutPatch = true; // documented prior bug
  const path1AutoClear = flags.autoSearchPendingMap[columnId] === false;

  // Path 2: page fetch pending → column preset supersedes → page finally skips
  flags = {
    autoSearchPendingMap: Object.fromEntries(columnIds.map((id) => [id, false])),
    columnFetchPendingMap: Object.fromEntries(columnIds.map((id) => [id, true])),
  };
  const page2 = own.beginPageLoad();
  flags = applyPendingOwnershipPatch(flags, page2.pendingPatch, columnIds);
  flags = applyPendingOwnershipPatch(
    flags,
    { setColumnFetchPending: { columnId, value: true } },
    columnIds,
  );
  const col2 = own.beginColumnLoad(columnId);
  flags = applyPendingOwnershipPatch(flags, col2.pendingPatch, columnIds);
  flags = applyPendingOwnershipPatch(
    flags,
    { setAutoSearchPending: { columnId, value: true } },
    columnIds,
  );
  // column finishes as owner
  if (own.isColumnCurrent(columnId, col2.columnGen)) {
    flags = applyPendingOwnershipPatch(
      flags,
      { setAutoSearchPending: { columnId, value: false } },
      columnIds,
    );
  }
  const path2FetchClear = flags.columnFetchPendingMap[columnId] === false;
  const path2AutoClear = flags.autoSearchPendingMap[columnId] === false;

  // Path 3: seed save must not clear a newer column owner's pending unconditionally
  const colA = own.beginColumnLoad(columnId);
  flags = applyPendingOwnershipPatch(
    flags,
    { setAutoSearchPending: { columnId, value: true } },
    columnIds,
  );
  const colB = own.beginColumnLoad(columnId); // newer
  flags = applyPendingOwnershipPatch(flags, colB.pendingPatch, columnIds);
  flags = applyPendingOwnershipPatch(
    flags,
    { setAutoSearchPending: { columnId, value: true } },
    columnIds,
  );
  // stale seed finally (colA) must NOT clear
  if (own.isColumnCurrent(columnId, colA.columnGen)) {
    flags = applyPendingOwnershipPatch(
      flags,
      { setAutoSearchPending: { columnId, value: false } },
      columnIds,
    );
  }
  const path3StaleDidNotClear = flags.autoSearchPendingMap[columnId] === true;
  if (own.isColumnCurrent(columnId, colB.columnGen)) {
    flags = applyPendingOwnershipPatch(
      flags,
      { setAutoSearchPending: { columnId, value: false } },
      columnIds,
    );
  }

  return {
    path1AutoClear,
    path1StuckWithoutPatch,
    path2FetchClear,
    path2AutoClear,
    path3StaleDidNotClear,
    finalFlags: flags,
  };
}
